import { Box, IconButton, Tooltip } from '@mui/material'
import Iconify from 'src/components/Iconify'
import { icons } from 'src/design-system'
import { MAP_EDGE_INSET, MAP_FOOT_INSET, MAP_LAYER } from './consts'

// ----------------------------------------------------------------------
// Put the map back on the fleet.
//
// **Alone, in the bottom right**, which is where a map keeps this control and
// nowhere else. It used to be parked inside the "where am I looking" surface
// in the top left, beside a city list, as if picking a city and framing the
// fleet were two settings of one thing. They are not: one is a place you
// choose and the other is the one button that answers *where are my cars*.
//
// **A target, not a pair of corner brackets.** It drew `fitToView`, whose
// brackets mean *make this fill the frame* — on a map, next to a map's own
// controls, that reads as full screen. `recentre` is the crosshair every map
// product draws for this, and the binding lives in the token source.
//
// The box, the radius, the hover fill, the focus ring and the touch hit floor
// are the IconButton override's, not restated here. What *is* stated is the
// floating furniture: an opaque ground, a hairline and the drop shadow, which
// a control standing on a basemap needs and a control in a toolbar does not.
// ----------------------------------------------------------------------

export type MapRecentreProps = {
  /** Frame every vehicle that has reported a position. */
  onRecentre: () => void
  /**
   * Why framing the fleet is not offered. A disabled control always sits next
   * to its reason, so this is the tooltip rather than a silent grey button.
   */
  disabledReason?: string
}

const LABEL = 'Show every vehicle'

const MapRecentre = ({ onRecentre, disabledReason }: MapRecentreProps) => {
  const unavailable = disabledReason !== undefined

  return (
    <Tooltip title={disabledReason ?? LABEL} placement="left">
      {/*
        `aria-disabled`, not `disabled`. A disabled button is not focusable
        and MUI hangs the tooltip on a wrapper span, which has no role to take
        a name, so the reason was reachable by hover and by nothing else. The
        design system says a disabled control sits next to its reason; this is
        how the reason reaches a keyboard.
      */}
      {/* The lift is on the frame, not on the button. A control standing on a
          photographic basemap has to read as a thing above it, which is a
          shadow — and R1 rejects a pressable that rests on one, because an
          elevation is a property of a surface and nothing in this console is
          pressed by pushing a shadow down. Same split the map marker takes:
          the frame carries the elevation, the button fills it exactly, and
          the rendered pixels are unchanged. */}
      <Box
        sx={{
          position: 'absolute',
          zIndex: MAP_LAYER.furniture,
          right: (theme) => theme.spacing(MAP_EDGE_INSET),
          bottom: (theme) => theme.spacing(MAP_FOOT_INSET),
          display: 'inline-flex',
          borderRadius: (theme) => theme.radii.control,
          boxShadow: (theme) => theme.customShadows.dropdown,
        }}
      >
        <IconButton
          size="large"
          onClick={unavailable ? undefined : onRecentre}
          aria-disabled={unavailable}
          aria-label={disabledReason ?? LABEL}
          data-testid="map-recentre"
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            ...(unavailable && {
              color: 'action.disabled',
              cursor: 'default',
            }),
          }}
        >
          <Iconify icon={icons.recentre} size="toolbar" solo />
        </IconButton>
      </Box>
    </Tooltip>
  )
}

export { MapRecentre }
